// Deck icon set — a thin wrapper over lucide-react (the same pack the studio
// editor uses, studio/src/client/lib/icons.tsx) so deck slides and the editor
// share one visual language and one place to swap the pack later.
//
// Deck defaults differ from the editor's: an icon inline in slide copy should
// inherit the surrounding TEXT SIZE (`size: '1em'`) and COLOUR (lucide strokes
// `currentColor` by default), so `<Check/>` next to a `Body` line sizes and
// colours itself, and `<Check color={accent}/>` or `size="2cqw"` overrides when
// a slide wants a standalone mark. Brand stroke weight is a touch heavier than
// lucide's default to sit with Neuzeit Grotesk.
//
// NAMED imports (not `import *`) so the single-file deck build tree-shakes to
// only the icons a deck actually uses. Add an icon here when a slide needs it;
// never import straight from 'lucide-react' in a deck (keeps the set curated
// and the defaults consistent).
import type { LucideIcon, LucideProps } from 'lucide-react';
import {
  Check as LCheck,
  X as LX,
  Plus as LPlus,
  Minus as LMinus,
  ArrowRight as LArrowRight,
  ArrowUpRight as LArrowUpRight,
  ArrowDownRight as LArrowDownRight,
  ChevronRight as LChevronRight,
  TrendingUp as LTrendingUp,
  TrendingDown as LTrendingDown,
  Circle as LCircle,
  Dot as LDot,
  Star as LStar,
  Sparkles as LSparkles,
  Zap as LZap,
  Target as LTarget,
  Clock as LClock,
  CircleAlert as LCircleAlert,
  Info as LInfo,
} from 'lucide-react';

const DEFAULTS = {
  size: '1em',
  strokeWidth: 1.75,
  'aria-hidden': true,
  style: { display: 'inline-block', verticalAlign: 'middle', flex: 'none' },
} satisfies LucideProps;

/** Wrap a lucide icon with the deck defaults, merging any caller style. */
const make =
  (Icon: LucideIcon) =>
  ({ style, ...props }: LucideProps) => <Icon {...DEFAULTS} {...props} style={{ ...DEFAULTS.style, ...style }} />;

export const Check = make(LCheck);
export const X = make(LX);
export const Plus = make(LPlus);
export const Minus = make(LMinus);
export const ArrowRight = make(LArrowRight);
export const ArrowUpRight = make(LArrowUpRight);
export const ArrowDownRight = make(LArrowDownRight);
export const ChevronRight = make(LChevronRight);
export const TrendingUp = make(LTrendingUp);
export const TrendingDown = make(LTrendingDown);
export const Circle = make(LCircle);
export const Dot = make(LDot);
export const Star = make(LStar);
export const Sparkles = make(LSparkles);
export const Zap = make(LZap);
export const Target = make(LTarget);
export const Clock = make(LClock);
export const CircleAlert = make(LCircleAlert);
export const Info = make(LInfo);
